import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import TopBar from '../components/TopBar';
import { useAuth } from '../stores/auth';

type Punto = {
  id: number;
  nombre: string;
  descripcion: string | null;
  lat: number;
  lng: number;
  categoria_id: number;
  categoria_nombre: string;
  categoria_icono: string | null;
  categoria_color: string;
  capa_id: number | null;
  capa_nombre: string | null;
  barrio_nombre: string | null;
  verificado: number;
};

type Atributo = { clave: string; valor: string; tipo: string };

type PuntoDetalle = Punto & { atributos: Atributo[] };

type Capa = {
  id: number | null;
  nombre: string;
  puntos: Punto[];
};

const BASE_URL =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  (location.hostname === 'localhost' ? 'http://localhost/ciudadapp/api' : '/api');

export default function MapaPOI() {
  const municipioId = useAuth((s) => s.ciudadano?.municipio_id);
  const mapRef      = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markersRef  = useRef<L.LayerGroup | null>(null);

  const [capas,      setCapas]      = useState<Capa[]>([]);
  const [activasIds, setActivasIds] = useState<Set<number | null>>(new Set());
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [selected,   setSelected]   = useState<Punto | null>(null);
  const [detalle,    setDetalle]    = useState<PuntoDetalle | null>(null);
  const [loadingDet, setLoadingDet] = useState(false);

  // ── Cargar puntos ────────────────────────────────────────────
  useEffect(() => {
    if (!municipioId) return;
    setLoading(true);
    fetch(`${BASE_URL}/poi-puntos?municipio_id=${municipioId}`)
      .then(r => r.json())
      .then(d => {
        if (!d.ok) { setError('No se pudieron cargar los puntos.'); return; }
        const puntos: Punto[] = d.data ?? [];
        const mapaCapas = new Map<number | null, Capa>();
        puntos.forEach(p => {
          const key = p.capa_id;
          if (!mapaCapas.has(key)) {
            mapaCapas.set(key, { id: key, nombre: p.capa_nombre ?? 'Sin capa', puntos: [] });
          }
          mapaCapas.get(key)!.puntos.push(p);
        });
        const lista = Array.from(mapaCapas.values());
        setCapas(lista);
        setActivasIds(new Set(lista.map(c => c.id)));
      })
      .catch(() => setError('Error de conexión.'))
      .finally(() => setLoading(false));
  }, [municipioId]);

  // ── Init mapa ────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    mapInstance.current = L.map(mapRef.current, { zoomControl: true }).setView([-38.5, -65.0], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(mapInstance.current);
    markersRef.current = L.layerGroup().addTo(mapInstance.current);

    if (municipioId) {
      fetch(`${BASE_URL}/municipios?id=${municipioId}`)
        .then(r => r.json())
        .then(d => {
          const lat = parseFloat(d.data?.latitud ?? '');
          const lng = parseFloat(d.data?.longitud ?? '');
          if (!isNaN(lat) && !isNaN(lng) && mapInstance.current) {
            mapInstance.current.setView([lat, lng], 14);
          }
        })
        .catch(() => {});
    }

    return () => {
      mapInstance.current?.remove();
      mapInstance.current = null;
    };
  }, []);

  // ── Invalidar tamaño al terminar la carga ─────────────────────
  useEffect(() => {
    if (!loading && mapInstance.current) {
      setTimeout(() => mapInstance.current?.invalidateSize(), 50);
    }
  }, [loading]);

  // ── Renderizar marcadores ─────────────────────────────────────
  useEffect(() => {
    const map = mapInstance.current;
    const layer = markersRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    capas.forEach(capa => {
      if (!activasIds.has(capa.id)) return;
      capa.puntos.forEach(p => {
        const color = p.categoria_color || '#3498db';
        const icono = p.categoria_icono || '📍';
        const marker = L.marker([p.lat, p.lng], {
          icon: L.divIcon({
            className: '',
            html: `<div style="
              background:${color};color:#fff;
              width:34px;height:34px;
              border-radius:50% 50% 50% 0;
              transform:rotate(-45deg);
              display:flex;align-items:center;justify-content:center;
              box-shadow:0 2px 6px rgba(0,0,0,.35);
              font-size:15px;border:2px solid rgba(255,255,255,.7)">
              <span style="transform:rotate(45deg)">${icono}</span>
            </div>`,
            iconSize: [34, 34],
            iconAnchor: [17, 34],
          }),
        });
        marker.on('click', () => { setDetalle(null); setSelected(p); });
        layer.addLayer(marker);
      });
    });
  }, [capas, activasIds]);

  const toggleCapa = (id: number | null) => {
    setActivasIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const abrirDetalle = async (p: Punto) => {
    setLoadingDet(true);
    setDetalle(null);
    try {
      const r = await fetch(`${BASE_URL}/poi-puntos/${p.id}`);
      const d = await r.json();
      if (d.ok) setDetalle({ ...p, atributos: d.data.atributos ?? [] });
    } catch { /* sin atributos extra */ }
    finally { setLoadingDet(false); }
  };

  const abrirMaps = (p: Punto) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`, '_blank');
  };

  const totalVisible = capas
    .filter(c => activasIds.has(c.id))
    .reduce((s, c) => s + c.puntos.length, 0);

  // ── Colores del chip de categoría ─────────────────────────────
  const chipStyle = (color: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: color + '22', color, border: `1px solid ${color}55`,
    borderRadius: 999, padding: '2px 9px', fontSize: '.72rem', fontWeight: 700,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: '#fff', position: 'relative' }}>
      <TopBar title="Mapa de la ciudad" back />

      {/* Overlays estado */}
      {loading && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,.85)' }}>
          <span style={{ fontSize: '.9rem', color: '#6b7280' }}>Cargando puntos…</span>
        </div>
      )}
      {error && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
          <span style={{ color: '#ef4444', fontSize: '.9rem' }}>{error}</span>
        </div>
      )}

      {/* Filtros de capa */}
      {!loading && !error && capas.length > 0 && (
        <div style={{ display: 'flex', gap: 8, padding: '8px 12px', overflowX: 'auto', flexShrink: 0, borderBottom: '1px solid #f3f4f6', background: '#f9fafb' }}>
          {capas.map(capa => {
            const activa = activasIds.has(capa.id);
            return (
              <button key={capa.id ?? 'sin-capa'} onClick={() => toggleCapa(capa.id)} style={{
                flexShrink: 0, padding: '5px 12px', borderRadius: 999,
                fontSize: '.75rem', fontWeight: 600, cursor: 'pointer', border: '1.5px solid',
                background: activa ? '#0d9488' : '#fff',
                color: activa ? '#fff' : '#6b7280',
                borderColor: activa ? '#0d9488' : '#d1d5db',
              }}>
                {capa.nombre} ({capa.puntos.length})
              </button>
            );
          })}
        </div>
      )}

      {/* Contador */}
      {!loading && !error && (
        <div style={{ padding: '2px 16px', fontSize: '.72rem', color: '#9ca3af', flexShrink: 0 }}>
          {totalVisible} punto{totalVisible !== 1 ? 's' : ''} visible{totalVisible !== 1 ? 's' : ''}
        </div>
      )}

      {/* Mapa */}
      <div ref={mapRef} style={{ flex: 1, minHeight: 0, zIndex: 0 }} />

      {/* ── Panel rápido al tocar un alfiler ── */}
      {selected && !detalle && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          borderTop: '1px solid #e5e7eb', background: '#fff',
          padding: '14px 16px 20px', boxShadow: '0 -3px 16px rgba(0,0,0,.15)',
          zIndex: 410,
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
        }}>
          {/* Fila encabezado */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                {selected.categoria_icono && <span style={{ fontSize: '1.2rem' }}>{selected.categoria_icono}</span>}
                <span style={{ fontWeight: 700, color: '#111827', fontSize: '.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selected.nombre}
                </span>
                {!!selected.verificado && (
                  <span style={{ fontSize: '.65rem', background: '#dcfce7', color: '#16a34a', borderRadius: 999, padding: '1px 7px', fontWeight: 700, flexShrink: 0 }}>✓ Verificado</span>
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: selected.descripcion ? 6 : 0 }}>
                <span style={chipStyle(selected.categoria_color || '#6b7280')}>
                  {selected.categoria_icono} {selected.categoria_nombre}
                </span>
                {selected.barrio_nombre && (
                  <span style={{ fontSize: '.72rem', color: '#6b7280', alignSelf: 'center' }}>📍 {selected.barrio_nombre}</span>
                )}
              </div>
              {selected.descripcion && (
                <p style={{ fontSize: '.78rem', color: '#4b5563', margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {selected.descripcion}
                </p>
              )}
            </div>
            <button onClick={() => setSelected(null)} style={{ color: '#9ca3af', fontSize: '1.4rem', lineHeight: 1, flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', marginTop: -2 }}>×</button>
          </div>

          {/* Botones de acción */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => abrirDetalle(selected)}
              disabled={loadingDet}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 10, border: '1.5px solid #0d9488',
                background: '#fff', color: '#0d9488', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer',
              }}
            >
              {loadingDet ? 'Cargando…' : '📋 Ver más'}
            </button>
            <button
              onClick={() => abrirMaps(selected)}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 10, border: 'none',
                background: '#0d9488', color: '#fff', fontWeight: 700, fontSize: '.82rem', cursor: 'pointer',
              }}
            >
              🗺️ Cómo llegar
            </button>
          </div>
        </div>
      )}

      {/* ── Modal detalle completo ── */}
      {detalle && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 500,
          background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-end',
        }} onClick={() => setDetalle(null)}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', background: '#fff', borderRadius: '18px 18px 0 0',
              maxHeight: '75dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
          >
            {/* Handle */}
            <div style={{ width: 36, height: 4, borderRadius: 2, background: '#d1d5db', margin: '12px auto 0' }} />

            {/* Cabecera */}
            <div style={{ padding: '12px 20px 10px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                    {detalle.categoria_icono && <span style={{ fontSize: '1.4rem' }}>{detalle.categoria_icono}</span>}
                    <span style={{ fontWeight: 800, color: '#111827', fontSize: '1rem' }}>{detalle.nombre}</span>
                    {!!detalle.verificado && (
                      <span style={{ fontSize: '.65rem', background: '#dcfce7', color: '#16a34a', borderRadius: 999, padding: '2px 8px', fontWeight: 700 }}>✓ Verificado</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    <span style={chipStyle(detalle.categoria_color || '#6b7280')}>
                      {detalle.categoria_icono} {detalle.categoria_nombre}
                    </span>
                    {detalle.barrio_nombre && (
                      <span style={{ fontSize: '.72rem', color: '#6b7280', alignSelf: 'center' }}>📍 {detalle.barrio_nombre}</span>
                    )}
                  </div>
                </div>
                <button onClick={() => setDetalle(null)} style={{ color: '#9ca3af', fontSize: '1.5rem', lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
              </div>
            </div>

            {/* Cuerpo scrolleable */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
              {detalle.descripcion && (
                <p style={{ fontSize: '.85rem', color: '#374151', marginBottom: 14, lineHeight: 1.5 }}>
                  {detalle.descripcion}
                </p>
              )}

              {detalle.atributos.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {detalle.atributos.map(a => (
                    <div key={a.clave} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderBottom: '1px solid #f3f4f6', paddingBottom: 7 }}>
                      <span style={{ fontSize: '.78rem', color: '#9ca3af', textTransform: 'capitalize' }}>{a.clave.replace(/_/g, ' ')}</span>
                      <span style={{ fontSize: '.82rem', color: '#111827', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>
                        {a.tipo === 'url'
                          ? <a href={a.valor} target="_blank" rel="noreferrer" style={{ color: '#0d9488' }}>{a.valor}</a>
                          : a.tipo === 'boolean'
                            ? (a.valor === '1' || a.valor === 'true' ? '✅ Sí' : '❌ No')
                            : a.valor}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {!detalle.descripcion && detalle.atributos.length === 0 && (
                <p style={{ fontSize: '.82rem', color: '#9ca3af', textAlign: 'center', marginTop: 16 }}>Sin información adicional.</p>
              )}
            </div>

            {/* Botón cómo llegar */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #f3f4f6', paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
              <button
                onClick={() => abrirMaps(detalle)}
                style={{
                  width: '100%', padding: '12px 0', borderRadius: 12, border: 'none',
                  background: '#0d9488', color: '#fff', fontWeight: 700, fontSize: '.88rem', cursor: 'pointer',
                }}
              >
                🗺️ Cómo llegar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
